import { useState } from "react";
import InfoFilledIcon from "../../assets/svg/info-filled-icon";
import SyncFailedIcon from "../../assets/svg/sync-failed-icon";
import { convertUtcToLocalString } from "../../utils/functions/convert-to-local-timezone";
import SyncFailedReasonModal from "./SyncFailedReasonModal";
import { SYNC_STATUS_LABELS } from "./constants";

interface SyncStatusIndicatorProps {
  syncStatus?: string | null;
  lastSyncedAt?: string | null;
  /**
   * The backend's `status_failed_reason`. Only a failed sync carries one; when it's there the
   * label turns into a button that opens the raw error, otherwise the indicator stays static.
   */
  failedReason?: string | null;
}

const SyncStatusIndicator = ({
  syncStatus,
  lastSyncedAt,
  failedReason,
}: SyncStatusIndicatorProps) => {
  const [isReasonOpen, setIsReasonOpen] = useState(false);

  const label = SYNC_STATUS_LABELS[syncStatus ?? ""];

  if (!label) return null;

  // last_synced_at is UTC ("2026-03-20T09:15:00Z") — shown in local time.
  const syncedAt = lastSyncedAt
    ? convertUtcToLocalString(lastSyncedAt, "d MMM, h:mm a")
    : "";

  const isFailed = syncStatus === "failed";
  const hasReason = isFailed && Boolean(failedReason);
  const text = syncedAt ? `${label} ${syncedAt}` : label;

  const labelText = (
    <p
      className={`text-sm font-normal leading-none truncate text-verba-indigo dark:text-verba-indigo-dk ${
        hasReason
          ? "group-hover:underline group-hover:text-verba-blue dark:group-hover:text-verba-blue-dk"
          : ""
      }`}
    >
      {text}
    </p>
  );

  return (
    <div className="flex items-center gap-[2px] shrink-0">
      {isFailed ? (
        <SyncFailedIcon className="shrink-0 w-[12px] h-[12px] text-verba-red-alert dark:text-verba-red-alert-dk" />
      ) : (
        <span className="shrink-0 w-1 h-1 rounded-full bg-verba-green-success dark:bg-verba-green-success-dk" />
      )}

      {hasReason ? (
        <>
          <button
            type="button"
            onClick={() => setIsReasonOpen(true)}
            className="group flex items-center gap-[2px] min-w-0 cursor-pointer"
          >
            {labelText}
            <InfoFilledIcon className="shrink-0 w-3 h-3 text-verba-indigo dark:text-verba-indigo-dk group-hover:text-verba-blue dark:group-hover:text-verba-blue-dk" />
          </button>
          <SyncFailedReasonModal
            open={isReasonOpen}
            onClose={() => setIsReasonOpen(false)}
            reason={failedReason ?? ""}
            syncedAt={syncedAt}
          />
        </>
      ) : (
        labelText
      )}
    </div>
  );
};

export default SyncStatusIndicator;
